/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Story View Page — Animated story for a specific document
 *
 * Displays the QR-linked animated story experience for a document code.
 * This is what users see when they scan the QR code from their printed report.
 * 
 * Redirects to the personalized Story UI when a valid docId is supplied.
 */

import { useParams, useSearchParams, Link, Navigate } from 'react-router-dom';
import { isValidDocId, extractDocId } from '../utils/docId';
import '../styles/interactive-story.css';

/**
 * Resolve docId from multiple sources for backward compatibility:
 * 1. Path parameter: /story/ui/:docId (new preferred format)
 * 2. Path parameter: /story/:code (legacy format)
 * 3. Query parameter: /story?docId=doc_xyz or /story/ui?docId=doc_xyz
 * 4. Legacy formats: extract docId if embedded in other formats
 */
function resolveDocId(pathCode: string | undefined, pathDocId: string | undefined, searchParams: URLSearchParams): string | null {
  // Try new route format first: /story/ui/:docId
  if (pathDocId) {
    const extracted = extractDocId(pathDocId);
    if (extracted && isValidDocId(extracted)) {
      return extracted;
    }
  }
  
  // Try legacy path parameter: /story/:code
  if (pathCode) {
    const extracted = extractDocId(pathCode);
    if (extracted && isValidDocId(extracted)) {
      return extracted;
    }
  }
  
  // Try query parameter
  const queryDocId = searchParams.get('docId');
  if (queryDocId) {
    const extracted = extractDocId(queryDocId);
    if (extracted && isValidDocId(extracted)) {
      return extracted;
    }
  }
  
  // No valid docId found
  return null;
}

function StoryViewPage() {
  const { code, docId: pathDocId } = useParams<{ code?: string; docId?: string }>();
  const [searchParams] = useSearchParams();

  // Resolve docId from path or query params
  const docId = resolveDocId(code, pathDocId, searchParams);

  if (!docId) {
    return (
      <div className="story-view-page">
        <div className="story-view-container">
          <div className="story-view-content" style={{
            padding: '2rem',
            background: '#fef2f2',
            border: '2px solid #fca5a5',
            borderRadius: '8px',
          }}>
            <h2 style={{ color: '#991b1b', marginBottom: '1rem' }}>
              ❌ Invalid Link Format
            </h2>
            <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>
              The link you're trying to access is malformed or in an incorrect format.
              Document IDs should look like: <code>doc_abc123xyz</code>
            </p>
            <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>
              If you received this link from a QR code or email, please contact support.
            </p>
            <Link 
              to="/story" 
              className="btn btn-primary"
              style={{ marginTop: '1rem', display: 'inline-block' }}
            >
              Create a new report →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Navigate
      to={`/story/ui?docId=${encodeURIComponent(docId)}&src=legacy`}
      replace
    />
  );
}

export default StoryViewPage;
